import React from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/app/AuthContext";
import { Activity } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  fetchLakeStatus,
  fetchRawWindow,
  fetchTelemetryEvents,
  fetchTelemetryWindow,
  rangeMinutes,
  RAW_WINDOW_MAX_MINUTES,
  SCAN_METRICS,
  scanKindsFor,
  scanShapeOf,
  VIEW_EVENT_KINDS,
  telemetryErrorCode,
  type RawFrameRow,
  type TelemetryBudgetMeta,
  type TelemetryChartMetric,
  type TelemetryEvent,
  type TelemetryEventKind,
  type TelemetryQueryMeta,
  type TelemetryWindowResult,
} from "@/lib/telemetry";
import { CoverageStrip } from "@/components/telemetry/CoverageStrip";
import { EventContextDrawer } from "@/components/telemetry/EventContextDrawer";
import { EventsTable } from "@/components/telemetry/EventsTable";
import { LakeConfigMismatch, LakeFreshness } from "@/components/telemetry/LakeFreshness";
import { QueryCost } from "@/components/telemetry/QueryCost";
import { RawFramesTable } from "@/components/telemetry/RawFramesTable";
import { SeriesCharts } from "@/components/telemetry/SeriesCharts";
import { ServiceFramesTable } from "@/components/telemetry/ServiceFramesTable";
import { SummaryStrip } from "@/components/telemetry/SummaryStrip";
import { TelemetryErrorPanel } from "@/components/telemetry/TelemetryError";
import { TimelineMap } from "@/components/telemetry/TimelineMap";
import { PRESETS, TelemetryControls, type PresetId } from "@/components/telemetry/TelemetryControls";
import { describeLag, formatSeconds, toIsoZ, type TelemetryTz } from "@/components/telemetry/format";

const DEFAULT_PRESET: PresetId = "6h";
const DEFAULT_KINDS: TelemetryEventKind[] = [...VIEW_EVENT_KINDS];
/** What a support engineer wants to see first: what the charger is drawing,
 *  what it is delivering, and how much it has delivered. Frequency stays
 *  available but off — it is a diagnosis, not a first read. */
const DEFAULT_METRICS: TelemetryChartMetric[] = [
  "current",
  "voltage",
  "power",
  "session_energy",
  "temperature",
];
const EVENTS_PAGE_LIMIT = 200;
/** The backend's own default for /raw-window; a page of frames, not a window's worth. */
const RAW_PAGE_LIMIT = 1000;
/** Newest first, like the decoded table — support reads a window backwards. */
const RAW_ORDER = "desc" as const;
/** Long enough that a support engineer can flip back and forth for half an
 *  hour without re-paying, short enough that idle tabs release the rows. */
const WINDOW_CACHE_MS = 30 * 60_000;
/** Shorter than the decoded window: raw frames are the heaviest rows the lake
 *  holds, and nobody should be holding an hour of them in a background tab. */
const RAW_CACHE_MS = 10 * 60_000;

interface EventsFeed {
  rows: TelemetryEvent[];
  cursor: string | null;
  queries: TelemetryQueryMeta[];
}

interface RawFeed {
  rows: RawFrameRow[];
  cursor: string | null;
  queries: TelemetryQueryMeta[];
}

/** Decoded is what the table means; raw is what the wire carried. */
type EventTableMode = "decoded" | "raw";

/**
 * Telemetry history for one charger, read from the S3 lake through the backend.
 *
 * Mounted only when its tab is open: an Athena scan costs money, and opening a
 * charger page must cost nothing. Everything on screen comes from
 * /api/telemetry/* — this component never talks to S3 or Athena, and never
 * computes a `unit_bucket`.
 */
export function UnitTelemetryPanel({ unitId }: { unitId: string }) {
  const { isAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const [runningQueryId, setRunningQueryId] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<TelemetryEvent | null>(null);
  const [feed, setFeed] = React.useState<EventsFeed | null>(null);
  const [pagingError, setPagingError] = React.useState<unknown>(null);
  const [isPaging, setIsPaging] = React.useState(false);
  // Which reading of the events the table is showing. Deliberately component
  // state and not a URL parameter: a link that carried `mode=raw` would buy an
  // Athena scan of the expensive column the moment it was opened, and raw mode
  // is meant to be a thing a support engineer asks for, not a thing a
  // bookmark does.
  const [tableMode, setTableMode] = React.useState<EventTableMode>("decoded");
  const [rawFeed, setRawFeed] = React.useState<RawFeed | null>(null);
  const [rawPagingError, setRawPagingError] = React.useState<unknown>(null);
  const [isRawPaging, setIsRawPaging] = React.useState(false);

  const preset = (params.get("preset") as PresetId) || DEFAULT_PRESET;
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const tz: TelemetryTz = params.get("tz") === "utc" ? "utc" : "local";
  const kinds = parseList<TelemetryEventKind>(params.get("kinds"), DEFAULT_KINDS);
  const metrics = parseList<TelemetryChartMetric>(params.get("metrics"), DEFAULT_METRICS);

  const patch = React.useCallback(
    (next: Record<string, string | null>, replace = false) => {
      setParams(
        (current) => {
          const merged = new URLSearchParams(current);
          for (const [key, value] of Object.entries(next)) {
            if (value === null) merged.delete(key);
            else merged.set(key, value);
          }
          return merged;
        },
        { replace },
      );
    },
    [setParams],
  );

  // A preset resolves to an ABSOLUTE window the moment it is picked, and stays
  // frozen until the next explicit refresh. A window recomputed on every render
  // would re-scan on every render, and a cursor walk needs a `to` that does not
  // move underneath it.
  const applyPreset = React.useCallback(
    (next: PresetId, replace = false) => {
      const spec = PRESETS.find((p) => p.id === next);
      if (!spec) {
        patch({ preset: "custom" }, replace);
        return;
      }
      const now = new Date();
      patch(
        {
          preset: next,
          from: toIsoZ(new Date(now.getTime() - spec.hours * 3_600_000)),
          to: toIsoZ(now),
        },
        replace,
      );
    },
    [patch],
  );

  React.useEffect(() => {
    if (!from || !to) applyPreset(preset === "custom" ? DEFAULT_PRESET : preset, true);
  }, [from, to, preset, applyPreset]);

  const kindsKey = kinds.join(",");

  // What the backend is asked for, and the ONLY thing besides the unit and the
  // window that may enter the query key. Everything else on the control bar is
  // a filter over rows this query already returned.
  const scanShape = scanShapeOf(kinds);
  const scanKinds = React.useMemo(() => scanKindsFor(scanShape), [scanShape]);

  const windowQ = useQuery({
    queryKey: ["telemetry", "window", unitId, from, to, scanShape],
    queryFn: ({ signal }) =>
      fetchTelemetryWindow(
        unitId,
        { from, to, kinds: scanKinds, metrics: SCAN_METRICS, limit: EVENTS_PAGE_LIMIT },
        { signal, onQueryRunning: setRunningQueryId },
      ),
    enabled: !!unitId && !!from && !!to,
    // A retry is another paid scan, and a background refetch is a bill nobody
    // asked for. Refresh is a button.
    retry: false,
    staleTime: Infinity,
    gcTime: WINDOW_CACHE_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const admin = isAdmin;

  // The whole window in one page of frames, or nothing: `/raw-window` refuses
  // anything wider than its cap, so a range the user has not narrowed is a
  // message, not a request.
  const windowMinutes = from && to ? rangeMinutes(from, to) : 0;
  const rawFits = windowMinutes > 0 && windowMinutes <= RAW_WINDOW_MAX_MINUTES;
  const rawMode = tableMode === "raw" && admin;

  const rawQ = useQuery({
    // The window, the unit and the page shape — never the fact that the table
    // happens to be showing this mode right now, so flipping back and forth
    // reads the same cached page.
    queryKey: ["telemetry", "raw-table", unitId, from, to, RAW_PAGE_LIMIT, RAW_ORDER],
    queryFn: ({ signal }) =>
      fetchRawWindow(
        unitId,
        { from, to, include_raw: true, limit: RAW_PAGE_LIMIT, order: RAW_ORDER },
        { signal, onQueryRunning: setRunningQueryId },
      ),
    enabled: rawMode && rawFits && !!unitId && !!from && !!to,
    // Same rules as the window scan, and more so — this one carries the raw
    // column. No retry, no background refetch, no refetch on re-enable.
    retry: false,
    staleTime: Infinity,
    gcTime: RAW_CACHE_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  React.useEffect(() => {
    if (!windowQ.isFetching && !rawQ.isFetching) setRunningQueryId(null);
  }, [windowQ.isFetching, rawQ.isFetching]);

  // A new window or a new charger is a new set of frames. The mode survives it
  // — a support engineer who narrowed the range while reading raw meant to read
  // the narrowed range raw.
  React.useEffect(() => {
    setRawFeed(null);
    setRawPagingError(null);
  }, [unitId, from, to]);

  // A new window or a new scan shape is a new walk. Pure view filters do not
  // reset or refetch; they only hide/show rows already held in memory.
  React.useEffect(() => {
    setFeed(null);
    setPagingError(null);
  }, [unitId, from, to, scanShape]);

  const statusQ = useQuery({
    queryKey: ["telemetry", "lake-status"],
    queryFn: () => fetchLakeStatus(),
    enabled: admin,
    retry: false,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const result = windowQ.data;
  const fetchedEvents = feed?.rows ?? result?.events ?? [];
  const visibleEvents = React.useMemo(
    () => fetchedEvents.filter((event) => kinds.includes(event.kind)),
    [fetchedEvents, kindsKey],
  );
  // Service frames come back on the same scan but are not support events, so
  // they are not one of the toggles above the table — they have their own
  // section, and the events table never mixes them in.
  const serviceEvents = React.useMemo(
    () => fetchedEvents.filter((event) => event.kind === "service"),
    [fetchedEvents],
  );
  const hasMore = feed ? !!feed.cursor : !!result?.nextCursor;

  /**
   * Continue the walk. Never re-open it.
   *
   * The first page is already on screen — from /diagnostics in compact mode,
   * from /events in split mode — and both mint a cursor under the same filter
   * hash, so paging is one request for one page in either mode. Re-fetching
   * page 1 to "get a usable cursor" would pay a round trip (and, on a cold
   * bundle, a scan) for rows the component is holding.
   */
  async function loadMoreEvents() {
    if (!result) return;
    const cursor = feed ? feed.cursor : result.nextCursor;
    if (!cursor) return;
    setIsPaging(true);
    setPagingError(null);
    try {
      const rows = feed ? feed.rows : result.events;
      const previousQueries = feed ? feed.queries : [];
      const page = await fetchTelemetryEvents(unitId, {
        from,
        to,
        kinds: scanKinds,
        limit: EVENTS_PAGE_LIMIT,
        order: "desc",
        cursor,
      });
      setFeed({
        rows: rows.concat(page.data ?? []),
        cursor: page.next_cursor,
        queries: previousQueries.concat(page.meta.query),
      });
    } catch (error) {
      setPagingError(error);
    } finally {
      setIsPaging(false);
    }
  }

  /** Continue the raw walk on the cursor `/raw-window` already minted. */
  async function loadMoreRaw() {
    const cursor = rawFeed ? rawFeed.cursor : (rawQ.data?.next_cursor ?? null);
    if (!cursor) return;
    setIsRawPaging(true);
    setRawPagingError(null);
    try {
      const rows = rawFeed ? rawFeed.rows : (rawQ.data?.data ?? []);
      const previousQueries = rawFeed ? rawFeed.queries : [];
      const page = await fetchRawWindow(unitId, {
        from,
        to,
        include_raw: true,
        limit: RAW_PAGE_LIMIT,
        order: RAW_ORDER,
        cursor,
      });
      setRawFeed({
        rows: rows.concat(page.data ?? []),
        cursor: page.next_cursor,
        queries: previousQueries.concat(page.meta.query),
      });
    } catch (error) {
      setRawPagingError(error);
    } finally {
      setIsRawPaging(false);
    }
  }

  const rawFrames = rawFeed?.rows ?? rawQ.data?.data ?? [];
  const rawHasMore = rawFeed ? !!rawFeed.cursor : !!rawQ.data?.next_cursor;
  const rawQueries = [
    ...(rawQ.data ? [rawQ.data.meta.query] : []),
    ...(rawFeed?.queries ?? []),
  ];

  const lakeMeta = result?.meta.lake ?? null;
  const queries = [...(result?.queries ?? []), ...(feed?.queries ?? [])];
  const configMismatch =
    telemetryErrorCode(windowQ.error) === "lake_config_mismatch"
      ? null // rendered by the error panel below, with the backend's own detail
      : statusQ.data?.config_match === false
        ? { expected: {}, found: statusQ.data.lake, mismatch: statusQ.data.mismatch ?? undefined }
        : null;

  return (
    <div className="space-y-4" data-testid="unit-telemetry-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Activity className="h-4 w-4" /> Telemetry history
        </h2>
        <span className="text-xs text-muted-foreground">
          Archived telemetry from the S3 lake — minutes to hours behind live. For “what is it
          doing right now”, use the live logs.
        </span>
      </div>

      <TelemetryControls
        unitId={unitId}
        preset={preset}
        from={from}
        to={to}
        kinds={kinds}
        metrics={metrics}
        tz={tz}
        isFetching={windowQ.isFetching}
        onPreset={(next) => applyPreset(next)}
        onRange={(range) => patch({ preset: "custom", from: range.from, to: range.to })}
        onKinds={(next) => patch({ kinds: (next.length ? next : DEFAULT_KINDS).join(",") })}
        onMetrics={(next) => patch({ metrics: next.join(",") })}
        onTz={(next) => patch({ tz: next })}
        onRefresh={() => {
          if (preset === "custom") windowQ.refetch();
          else applyPreset(preset);
        }}
      />

      {configMismatch && <LakeConfigMismatch detail={configMismatch} />}

      {runningQueryId && (
        <div
          data-testid="telemetry-query-running"
          className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs"
        >
          Query running in Athena (<span className="font-mono">{runningQueryId}</span>) — collecting
          the result…
        </div>
      )}

      {windowQ.isError ? (
        <TelemetryErrorPanel error={windowQ.error} unitId={unitId} />
      ) : windowQ.isLoading || !result ? (
        <LoadingSkeleton />
      ) : (
        <>
          <LakeFreshness lake={lakeMeta} status={statusQ.data ?? null} tz={tz} unitId={unitId} />

          <SummaryStrip
            summary={result.summary}
            coverage={result.coverage}
            eventCount={visibleEvents.length}
            tz={tz}
          />

          <EmptyWindowNote result={result} />

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <TimelineMap
                from={from}
                to={to}
                series={result.series}
                events={visibleEvents}
                bucketSeconds={result.meta.bucket_seconds}
                tz={tz}
                onSelectRange={(range) => patch({ preset: "custom", ...range })}
                onSelectEvent={setSelected}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Metrics</CardTitle>
            </CardHeader>
            <CardContent>
              <SeriesCharts
                series={result.series}
                metrics={metrics}
                bucketSeconds={result.meta.bucket_seconds}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-sm">Events</CardTitle>
                {/* Raw frames are an internal interface, so the switch itself is
                    admin-only — there is no mode here a tenant may enter. */}
                {admin && (
                  <ToggleGroup
                    type="single"
                    size="sm"
                    variant="outline"
                    value={tableMode}
                    onValueChange={(value) => value && setTableMode(value as EventTableMode)}
                    data-testid="telemetry-table-mode"
                  >
                    <ToggleGroupItem value="decoded">Decoded</ToggleGroupItem>
                    <ToggleGroupItem value="raw">Raw</ToggleGroupItem>
                  </ToggleGroup>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {rawMode ? (
                <RawTableSection
                  fits={rawFits}
                  minutes={windowMinutes}
                  unitId={unitId}
                  tz={tz}
                  isLoading={rawQ.isLoading}
                  isError={rawQ.isError}
                  error={rawQ.error}
                  budget={rawQ.data?.meta.budget}
                  truncated={rawQ.data?.meta.truncated}
                  frames={rawFrames}
                  hasMore={rawHasMore}
                  isLoadingMore={isRawPaging}
                  onLoadMore={loadMoreRaw}
                  pagingError={rawPagingError}
                  queries={rawQueries}
                  onNarrow={() =>
                    patch({
                      preset: "custom",
                      from: toIsoZ(
                        new Date(new Date(to).getTime() - RAW_WINDOW_MAX_MINUTES * 60_000),
                      ),
                      to,
                    })
                  }
                />
              ) : (
                <>
                  {pagingError ? <TelemetryErrorPanel error={pagingError} unitId={unitId} /> : null}
                  <EventsTable
                    events={visibleEvents}
                    tz={tz}
                    isLoading={windowQ.isFetching && !result}
                    hasMore={hasMore}
                    isLoadingMore={isPaging}
                    onLoadMore={loadMoreEvents}
                    onSelect={setSelected}
                    totalEvents={result.totalEvents}
                  />
                </>
              )}
            </CardContent>
          </Card>

          {serviceEvents.length > 0 && (
            <Card data-testid="telemetry-service-frames">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">
                  Service frames ({serviceEvents.length.toLocaleString()})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="text-[11px] text-muted-foreground">
                  Frames that carry no telemetry — debug, meter id, and frames rejected on
                  checksum. Kept out of the charts and the events above; nothing here is a
                  reading.
                </div>
                <ServiceFramesTable events={serviceEvents} tz={tz} onSelect={setSelected} />
              </CardContent>
            </Card>
          )}

          {/* Last of the views, and deliberately so: coverage answers "did the
              archive get the frames", which is a question about the lake, not
              about the charger. Support opens this page to read state and
              metrics first. It stays clickable — a cell is still the fastest
              way to narrow the window. */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                Coverage by {result.coverage.some((c) => c.hour !== undefined) ? "hour" : "day"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CoverageStrip
                cells={result.coverage}
                tz={tz}
                onSelectRange={(range) => patch({ preset: "custom", ...range })}
              />
            </CardContent>
          </Card>

          <QueryCost
            queries={queries}
            budget={result.meta.budget}
            mode={result.meta.mode}
            truncated={result.meta.truncated}
          />
        </>
      )}

      <EventContextDrawer
        open={!!selected}
        unitId={unitId}
        event={selected}
        tz={tz}
        isAdmin={admin}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

/**
 * The events card in raw mode.
 *
 * Two things this section refuses to do. It never fires a query for a window
 * `/raw-window` would reject — the cap is a property of the endpoint, and
 * bouncing off a 400 to learn it is a round trip that teaches the user nothing
 * a sentence cannot. And it never widens the window to fit: narrowing is the
 * user's decision, so the quick action offers the last hour OF THE WINDOW ALREADY
 * SELECTED and leaves the click to them.
 */
function RawTableSection({
  fits,
  minutes,
  unitId,
  tz,
  isLoading,
  isError,
  error,
  budget,
  truncated,
  frames,
  hasMore,
  isLoadingMore,
  onLoadMore,
  pagingError,
  queries,
  onNarrow,
}: {
  fits: boolean;
  minutes: number;
  unitId: string;
  tz: TelemetryTz;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  budget?: TelemetryBudgetMeta;
  truncated?: boolean;
  frames: RawFrameRow[];
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  pagingError: unknown;
  queries: TelemetryQueryMeta[];
  onNarrow: () => void;
}) {
  if (!fits) {
    return (
      <div
        data-testid="telemetry-raw-too-wide"
        className="space-y-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm"
      >
        <div>
          Raw view is available for windows up to {RAW_WINDOW_MAX_MINUTES} min. Narrow the
          timeline/range first — this window is {formatSeconds(minutes * 60)}.
        </div>
        <Button variant="outline" size="sm" onClick={onNarrow} data-testid="telemetry-raw-narrow">
          Narrow to the last {RAW_WINDOW_MAX_MINUTES} min of this window
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="text-[11px] text-muted-foreground" data-testid="telemetry-raw-note">
        Every archived frame in this window, exactly as the charger sent it — one Athena scan of
        the raw column, run because you asked for it and never prefetched.
      </div>
      {isError ? (
        <TelemetryErrorPanel error={error} unitId={unitId} />
      ) : (
        <>
          {pagingError ? <TelemetryErrorPanel error={pagingError} unitId={unitId} /> : null}
          <RawFramesTable
            frames={frames}
            tz={tz}
            isLoading={isLoading}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={onLoadMore}
          />
          {queries.length > 0 && (
            <QueryCost queries={queries} budget={budget} mode="point" truncated={truncated} />
          )}
        </>
      )}
    </>
  );
}

/**
 * "No rows" is three different statements, and saying the wrong one manufactures
 * an incident on a healthy charger.
 */
function EmptyWindowNote({ result }: { result: TelemetryWindowResult }) {
  const samples = result.summary?.samples ?? result.coverage.reduce((sum, c) => sum + c.samples, 0);
  if (samples > 0) return null;

  const lake = result.meta.lake;
  const pending = lake?.pending_files ?? 0;
  const notArchived = result.coverage.some((c) => c.status === "not_archived");
  const unknown = lake?.status_available === false;

  let text: string;
  if (unknown) {
    text =
      "No rows in this window, and the feeder status could not be read — whether this is “nothing archived yet” or “the charger was silent” is unknown.";
  } else if (pending > 0) {
    text = `Archiving in progress — ${pending} files pending. This is not “no data”.`;
  } else if (notArchived) {
    const lag = describeLag(lake?.lag_seconds ?? null);
    text =
      lag.kind === "future"
        ? `Not archived yet — and the lake watermark is ${formatSeconds(lag.aheadSeconds)} ahead of now, which means the feeder's source timezone does not match the host that wrote the logs. This is not “no data”.`
        : `Not archived yet — lake lag: ${lag.text}. This is not “no data”.`;
  } else {
    text =
      "No telemetry archived for this charger in this window — the charger was silent here. Check the live logs for the current picture.";
  }

  return (
    <div
      data-testid="telemetry-empty-note"
      className="rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm"
    >
      {text}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-3" data-testid="telemetry-loading">
      <Skeleton className="h-10 w-full" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-14 w-full" />
        ))}
      </div>
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-44 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function parseList<T extends string>(raw: string | null, fallback: T[]): T[] {
  if (!raw) return fallback;
  const parsed = raw.split(",").map((part) => part.trim()).filter(Boolean) as T[];
  return parsed.length ? parsed : fallback;
}
